import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiErrorResponse, ApiResponse, ApiServiceInterface } from '@hutsix/ngxh6';
import { PermitViewGrantService } from '../../../services/permit-view-grant.service';

interface MyPermitRow {
    permitNumber?: string | null;
    permitType: string;
    status: string;
    statusLabel?: string;
    accessStartDate?: string | null;
    accessEndDate?: string | null;
    token: string;
}

interface MyPermitsResponse {
    email: string;
    permits: MyPermitRow[];
}

/**
 * "My Permits" for applicants, who have no login.
 *
 * All of someone's permits already hang off one account (public submissions reuse the
 * account matching the email), so proving that email once is enough to list them.
 * There are two ways in, and both end up with an account grant on this device:
 *
 *  - already proved it: confirmed the email on a permit, or just submitted an
 *    application, so a grant is sitting in storage;
 *  - hasn't, or is on a new device: enters their email and we send a link
 *    (/my-permits/:grant). The mailbox is what proves who they are.
 *
 * The grant is swapped out of the URL immediately on arrival so it doesn't linger in
 * history or get shared by copy-paste.
 */
@Component({
    selector: 'app-my-permits',
    templateUrl: './my-permits.component.html',
    styleUrls: ['./my-permits.component.scss'],
    standalone: false,
})
export class MyPermitsComponent implements OnInit {
    public permits: MyPermitRow[] = [];
    public accountEmail?: string;
    public loading = true;

    // Email-request form (shown when this device has no grant)
    public needsEmail = false;
    public email = '';
    public sending = false;
    public sent = false;
    public error?: string | null;

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        private route: ActivatedRoute,
        private router: Router,
        private grants: PermitViewGrantService,
    ) {}

    ngOnInit(): void {
        // Arriving from the emailed link: adopt the grant, then drop it from the URL.
        const fromLink = this.route.snapshot.paramMap.get('grant');
        if (fromLink) {
            this.grants.rememberAccount(fromLink, this.expiryFrom(fromLink));
            this.router.navigate(['/my-permits'], { replaceUrl: true });
        }

        const grant = this.grants.readAccount();
        if (!grant) {
            this.loading = false;
            this.needsEmail = true;
            return;
        }
        this.load(grant);
    }

    private load(grant: string): void {
        this.api.post({ url: '/api/public/permit/mine/list', data: { grant }, skipAuth: true, displayErrors: false }).subscribe(
            (res: ApiResponse<MyPermitsResponse>) => {
                this.loading = false;
                this.permits = res.data?.permits ?? [];
                this.accountEmail = res.data?.email;
            },
            (_err: ApiErrorResponse<any>) => {
                // Expired or rejected - fall back to asking for the email again.
                this.grants.clearAccount();
                this.loading = false;
                this.needsEmail = true;
            },
        );
    }

    /** Ask us to email a link to this address. */
    public requestLink(): void {
        if (!this.email || this.sending) return;
        this.sending = true;
        this.error = null;
        this.api
            .post({
                url: '/api/public/permit/mine/request-link',
                data: { email: this.email },
                skipAuth: true,
                displayErrors: false,
            })
            .subscribe(
                () => {
                    this.sending = false;
                    this.sent = true;
                },
                (err: ApiErrorResponse<any>) => {
                    this.sending = false;
                    this.error =
                        err?.error?.status === 429
                            ? 'Too many requests - please wait a while before asking for another link.'
                            : 'Sorry, something went wrong. Please try again.';
                },
            );
    }

    /** Re-lock this device - for shared or borrowed computers. */
    public forgetDevice(): void {
        this.grants.clearAccount();
        this.permits = [];
        this.accountEmail = undefined;
        this.needsEmail = true;
        this.sent = false;
    }

    /**
     * The grant's own expiry, so a stored copy lapses exactly when the server stops
     * accepting it. Format is "<accountId>.<expiry>.<signature>"; anything unexpected
     * falls back to a short window rather than trusting the value.
     */
    private expiryFrom(grant: string): number {
        const parts = grant.split('.');
        const expiry = parts.length === 3 ? Number(parts[1]) : NaN;

        return Number.isFinite(expiry) && expiry > 0 ? expiry : Math.floor(Date.now() / 1000) + 3600;
    }
}
